package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 초대 대상 종류. 문자열 값은 `functions/src/invitations.ts`의 `InviteeType`과 동일하다.
 *
 * [GUARDIAN]만 `members/{uid}` 문서를 받지 않는다 — 보호자는 학원 멤버가 아니라
 * `guardianLinks`로만 연결되고 알림 팬아웃 범위만 얻는다(§2.6).
 */
@Serializable
enum class InviteeType(val displayName: String) {
    STUDENT("학생"),
    GUARDIAN("보호자"),
    TEACHER("선생님"),
}

/**
 * 초대 코드 발급 요청.
 *
 * 서버가 요구하는 권한이 대상에 따라 다르다(`invitations.ts`) — [TEACHER][InviteeType.TEACHER]는
 * 구성원 관리라 ADMIN, 학생·보호자는 선생님의 일상 업무라 EDITOR면 된다.
 */
@Serializable
data class CreateInvitationRequest(
    val academyId: String,
    val inviteeType: InviteeType,
    /**
     * 연결할 학생 문서 id. 학생·보호자 초대에는 **필수**, 선생님 초대에는 **금지**다
     * (서버가 둘 다 `invalid-argument`로 거부한다).
     */
    val targetId: String? = null,
    /** 보호자 초대 전용 호칭. null이면 서버가 "보호자"로 채운다. */
    val relation: String? = null,
    /** null이면 서버 기본값 72시간. 서버 허용 범위는 1시간~14일이다. */
    val expiresInHours: Int? = null,
)

/**
 * 발급된 초대. [code]가 곧 `edubridge_invitations` 문서 id라서 수락은 단일 포인트 읽기다.
 *
 * 코드 알파벳은 Crockford 계열(O/I/L/U 제외)이라 구두로 불러 줘도 오독되지 않는다.
 */
@Serializable
data class InvitationResult(
    val code: String? = null,
    val academyId: String? = null,
    val inviteeType: InviteeType? = null,
    val targetId: String? = null,
    /** ISO-8601. 이 시각 이후의 수락은 서버가 거부한다. */
    val expiresAt: String? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null
}

/**
 * 초대 확인의 비소비 결과. 초대 코드는 수락 권한 자체이므로 [USABLE]에서 학원 정보를 보여 주되,
 * 타인이 이미 쓴 경우에는 누가 썼는지 또는 어느 학원 코드였는지는 절대 내리지 않는다.
 */
@Serializable
enum class InvitationPreviewStatus {
    /** 아직 수락할 수 있다. 확인 화면에서 사용자가 수락을 명시적으로 선택해야 한다. */
    USABLE,
    /** 현재 로그인 계정이 이미 이 초대를 수락했다. 해당 학원으로 이동할 수 있다. */
    ALREADY_ACCEPTED_BY_CALLER,
    /** 다른 계정이 이미 수락했다. 다른 사람의 식별 정보는 반환하지 않는다. */
    ALREADY_USED,
    /** 존재하지 않음·만료·비정상 상태를 같은 결과로 접어 코드 열거를 막는다. */
    UNUSABLE,
}

/**
 * 초대 확인 결과. 이 호출은 초대를 소비하지 않는다.
 *
 * [USABLE]과 [ALREADY_ACCEPTED_BY_CALLER]일 때만 학원 정보와 서버의 [expiresAt]을 준다.
 * 클라이언트는 이 시각까지 코드를 보관하되, 확인 화면을 보여 준 뒤에만 [acceptInvitation]을
 * 호출해야 한다. [ALREADY_USED]·[UNUSABLE]은 민감한 대상 정보를 싣지 않는다.
 */
@Serializable
data class InvitationPreviewResult(
    val status: InvitationPreviewStatus = InvitationPreviewStatus.UNUSABLE,
    val academyId: String? = null,
    val academyName: String? = null,
    val inviteeType: InviteeType? = null,
    /** 서버가 정한 ISO-8601 만료 시각. 로컬 보관 기한도 이 값에 맞춘다. */
    val expiresAt: String? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null
}

/**
 * 초대 수락 결과. **인증된 계정을 학원 구성원으로 바꾸는 유일한 경로다**(§9.4).
 *
 * 학생 초대를 수락하면 서버가 `students/{targetId}.userId`를 호출자 uid로 채운다 —
 * QR 출결(`scanAttendance`)이 요구하는 "ACTIVE STUDENT 멤버십 + 계정이 연결된 학생 레코드"가
 * 여기서 비로소 성립한다(§3.4-4).
 *
 * [replayed]는 같은 계정이 같은 코드를 다시 수락한 경우다 — 단일 사용이지만 재시도는 안전하다.
 */
@Serializable
data class AcceptInvitationResult(
    val academyId: String? = null,
    val academyName: String? = null,
    val inviteeType: InviteeType? = null,
    /** 보호자 수락이면 null — 보호자는 역할을 받지 않는다. */
    val role: UserRole? = null,
    val permission: Permission? = null,
    /** 학생·보호자 초대에서 연결된 학생 문서 id. */
    val studentId: String? = null,
    /** 보호자 수락에서 만들어진 `guardianLinks` 문서 id. */
    val guardianLinkId: String? = null,
    val replayed: Boolean = false,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null
}
